import React, { memo, useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  VStack,
} from "@chakra-ui/react";
import {
  MessageFragment,
  MessageThreadFragment,
  MessageType,
  MessageWithMessageThreadQuery,
  MessageWithMessageThreadQueryVariables,
} from "gql/graphql";
import MESSAGE_WITH_MESSAGE_THREAD_QUERY from "modules/messaging/_networking/message-with-message-thread";
import LoadingModal from "./loading-modal";
import { useOverlay } from "modules/providers/overlay-provider";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { useFile } from "modules/providers/file-provider";
import DropFilesHere from "modules/messaging/_shared/drop-files-here";
import { useDropzone } from "react-dropzone";
import useEditMessage from "modules/hooks/use-edit-message";
import QuillEditor from "modules/messaging/compose/quill-editor";
import useQuill from "modules/quill/use-quill";
import ComposeQuillToolbar from "modules/messaging/compose/quill-toolbar/compose-quill-toolbar";
import ComposeBottomToolbar from "modules/messaging/compose/compose-bottom-toolbar";
import ComposeAttachmentList from "modules/messaging/compose/compose-attachment-list";
import colors from "app/colors";
import useUserPreference from "modules/hooks/use-user-preference";
import { useAuth } from "modules/providers/auth-provider";

interface IProps {
  messageId: string;
}

const EditMessageModal = ({ messageId }: IProps) => {
  const draftId = messageId;
  const { closeModal } = useOverlay();

  const { data } = useQuery<
    MessageWithMessageThreadQuery,
    MessageWithMessageThreadQueryVariables
  >(MESSAGE_WITH_MESSAGE_THREAD_QUERY, {
    variables: {
      id: messageId,
    },
  });

  const { uploadFiles } = useFile();

  const onFileListUpload = useCallback(
    (files: File[]) => uploadFiles(draftId, files),
    [draftId, uploadFiles],
  );

  const { getRootProps, isDragActive } = useDropzone({
    onDrop: onFileListUpload,
    noClick: true,
  });

  const message = useMemo(() => data?.messages[0], [data]);

  const messageThread = useMemo(() => data?.messages[0]?.messageThread, [data]);

  const title = useMemo(() => {
    if (message?.type === MessageType.Task) {
      return "Edit Task Description";
    }
    return `Edit Message`;
  }, [message]);

  if (!message || !messageThread) return <LoadingModal />;

  return (
    <Modal isOpen onClose={closeModal} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent {...getRootProps()}>
        <DropFilesHere isDragActive={isDragActive} />
        <ModalHeader fontFamily="Outfit" fontSize={20} fontWeight={700}>
          {title}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <EditMessage messageThread={messageThread} message={message} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

interface IEditMessageProps {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
}

const EditMessage: React.FC<IEditMessageProps> = ({
  messageThread,
  message,
}) => {
  const { me } = useAuth();
  const draftId = message.id;
  const { editMessage, isSending } = useEditMessage(message);
  const { closeModal } = useOverlay();

  const { stringValue: enterKeySetting } = useUserPreference({
    userId: me?.id,
    name: "ENTER_KEY_SETTING",
  });

  const {
    isDraftLoaded,
    draftExists,
    setEditMessageDraft,
    updateMessage,
    createDraft,
    deleteDraft,
  } = useMessageThreadDraft({
    draftId,
  });

  const initMessageDraft = useCallback(async () => {
    await createDraft(
      {
        type: messageThread.type,
        draftType: "edit-message",
      },
      true,
    );
    await setEditMessageDraft(message);
  }, [createDraft, message, messageThread.type, setEditMessageDraft]);

  useEffect(() => {
    if (draftExists === undefined) return;
    if (draftExists) return;
    initMessageDraft();
  }, [draftExists, initMessageDraft]);

  const { uploadFiles } = useFile();

  const onFilesAttached = useCallback(
    (files: File[]) => uploadFiles(draftId, files),
    [draftId, uploadFiles],
  );

  const onEditMessage = useCallback(async () => {
    if (isSending) return;
    await editMessage();
    await deleteDraft();
    closeModal();
  }, [isSending, editMessage, deleteDraft, closeModal]);

  const placeholder = useMemo(() => {
    if (message?.type === MessageType.Task) {
      return "What needs to be done?";
    }
    return `Edit Message`;
  }, [message]);

  const onChange = useCallback(
    (delta: string, plainText: string) => {
      updateMessage(delta, plainText);
    },
    [updateMessage],
  );

  const {
    containerRef,
    toolbarRef,
    appendText,
    appendMention,
    format,
    insertLink,
    focus,
    activeFormats,
    clickedMention,
    closeClickedMention,
  } = useQuill({
    placeholder,
    onChange,
    defaultValue: message.quillDelta,
    isReady: isDraftLoaded,
    autoFocus: true,
    onEnter: enterKeySetting === "send-message" ? onEditMessage : undefined,
    type: messageThread.type,
    onCtrlEnter: onEditMessage,
    onFilePaste: onFilesAttached,
  });

  return (
    <Stack maxH={600}>
      <ComposeQuillToolbar
        ref={toolbarRef}
        format={format}
        insertLink={insertLink}
        activeFormats={activeFormats}
      />
      <VStack
        w="full"
        spacing={0}
        rounded={4}
        borderColor={colors.grayPrimary}
        borderWidth={1}
        px={2}
        minH={0}
        _focusWithin={{
          outline: "2px solid",
          outlineColor: colors.blue,
        }}
      >
        <QuillEditor
          containerRef={containerRef}
          focus={focus}
          draftId={draftId}
          clickedMention={clickedMention}
          onClose={closeClickedMention}
          isExpanded
        />
        <ComposeAttachmentList draftId={draftId} />
      </VStack>
      <ComposeBottomToolbar
        draftId={draftId}
        appendText={appendText}
        appendMention={appendMention}
        onSend={onEditMessage}
        isSending={isSending}
        messageThread={messageThread}
      />
    </Stack>
  );
};

export default memo(EditMessageModal);
